import { Component, OnInit } from '@angular/core';
import { PricingService } from 'src/app/services/pricing.service';

interface GarmentPrice {
  price?: number;
  currencySymbol?: string;
  isWebVisible?: number;
}

interface GarmentItem {
  garmentName?: string;
  prices?: GarmentPrice[];
}

interface PricingCategory {
  key: string;
  label: string;
}

interface PricingLineItem {
  name: string;
  displayPrice: string;
}

type PricingPlan = 'onetime' | 'subscription' | 'business';

@Component({
  selector: 'app-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss']
})
export class PricingComponent implements OnInit {
  loading = true;
  error = '';
  activePlan: PricingPlan = 'onetime';
  rawResponse: any;
  categoryMap: { [key: string]: GarmentItem[] } = {};
  categories: PricingCategory[] = [];
  activeCategoryKey = '';
  activeItems: PricingLineItem[] = [];

  constructor(private pricingService: PricingService) {}

  ngOnInit(): void {
    this.loadPricing();
  }

  private loadPricing(): void {
    this.loading = true;
    this.error = '';

    this.pricingService.getGarmentsInfo().subscribe(
      (response: any) => {
        this.rawResponse = response;
        this.categoryMap = this.extractCategoryMap(response);
        this.categories = this.buildCategoryList(this.categoryMap);
        this.activeCategoryKey = this.getDefaultCategoryKey(this.categories);
        this.activeItems = this.buildActiveItems(this.activeCategoryKey);
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.error = 'Unable to load pricing right now. Please try again later.';
      }
    );
  }

  private extractCategoryMap(response: any): { [key: string]: GarmentItem[] } {
    if (response && response.data && typeof response.data === 'object') {
      return response.data;
    }

    if (response && typeof response === 'object') {
      return response;
    }

    return {};
  }

  private buildCategoryList(map: { [key: string]: GarmentItem[] }): PricingCategory[] {
    const categories = Object.keys(map)
      .filter((key) => key !== 'adjustments')
      .filter((key) => Array.isArray(map[key]) && this.buildActiveItems(key).length > 0)
      .map((key) => ({
        key,
        label: this.formatCategoryLabel(key)
      }));

    const washFoldIndex = categories.findIndex((category) => category.key === 'wash&Fold');

    if (washFoldIndex > -1) {
      const [washFoldCategory] = categories.splice(washFoldIndex, 1);
      categories.push(washFoldCategory);
    }

    return categories;
  }

  private getDefaultCategoryKey(categoryList: PricingCategory[]): string {
    if (!categoryList.length) {
      return '';
    }

    const dryCleaningCategory = categoryList.find((category) => category.key === 'dryCleaning');
    if (dryCleaningCategory) {
      return dryCleaningCategory.key;
    }

    return categoryList[0].key;
  }

  private buildActiveItems(categoryKey: string): PricingLineItem[] {
    const garments = this.categoryMap[categoryKey] || [];

    return garments
      .map((garment) => {
      const primaryPrice = this.getPrimaryPrice(garment.prices || []);
      if (!primaryPrice) {
        return null;
      }

      const name = garment && garment.garmentName ? garment.garmentName : '-';
      return {
        name,
        displayPrice: this.formatPrice(primaryPrice)
      };
    })
      .filter((item): item is PricingLineItem => !!item);
  }

  private getPrimaryPrice(prices: GarmentPrice[]): GarmentPrice | null {
    if (!prices.length) {
      return null;
    }

    const webVisible = prices.find((price) => Number(price.isWebVisible) === 1);
    return webVisible || null;
  }

  private formatPrice(priceItem: GarmentPrice | null): string {
    if (!priceItem || priceItem.price === null || priceItem.price === undefined) {
      return '-';
    }

    const currency = priceItem.currencySymbol || '$';
    const amount = Number(priceItem.price);

    if (Number.isNaN(amount)) {
      return '-';
    }

    return `${currency}${amount.toFixed(2)}`;
  }

  private formatCategoryLabel(key: string): string {
    if (!key) {
      return '';
    }

    return key
      .replace(/&/g, ' & ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/(^\w|\s\w)/g, (match) => match.toUpperCase());
  }

  selectCategory(categoryKey: string): void {
    if (!categoryKey || categoryKey === this.activeCategoryKey) {
      return;
    }

    this.activeCategoryKey = categoryKey;
    this.activeItems = this.buildActiveItems(categoryKey);
  }

  isCategoryActive(categoryKey: string): boolean {
    return this.activeCategoryKey === categoryKey;
  }

  selectPlan(plan: PricingPlan): void {
    this.activePlan = plan;
  }

  isPlanActive(plan: PricingPlan): boolean {
    return this.activePlan === plan;
  }

  getActiveCategoryLabel(): string {
    const activeCategory = this.categories.find((category) => category.key === this.activeCategoryKey);
    return activeCategory ? activeCategory.label : 'Category';
  }

  trackByIndex(index: number): number {
    return index;
  }
}
